import { AvatarProps, Box } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import SharedAssigneeSelect from '~components/shared/form/shared-assignee-select'
import {
    ChatPreviewFieldsFragment,
    useGetClinicAdminsAssigneeOptionsQuery,
    UserBasicInfoFragment,
    useUpdateChatMutation,
} from '~graphql/generated/graphql'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'

export default function ChatAssignee({
    chat,
    size = 'sm',
}: {
    chat: ChatPreviewFieldsFragment
    size?: AvatarProps['size']
}) {
    const toasts = useToastFeedback(
        'Successfully updated assignee',
        'Error updating assignee',
    )
    const { data, loading } = useGetClinicAdminsAssigneeOptionsQuery({
        variables: {
            clinicId: chat.clinic?.id || '',
        },
        skip: !chat.clinic?.id,
        fetchPolicy: 'cache-first',
    })

    const [updateChat, { loading: isUpdating }] = useUpdateChatMutation()

    async function handleSetAssignee(
        user: UserBasicInfoFragment | null,
    ): Promise<void> {
        if (user?.id === chat.assignee?.id) {
            toasts.onCompleted()
            return
        }

        await updateChat({
            variables: {
                id: chat.id,
                data: { assignee: user?.id ?? null },
            },
            ...toasts,
        })
    }

    const userOptions =
        data?.getClinicAdminsByClinic.map(cAdmin => cAdmin.user) ?? []

    const orderedUserOptions = useMemo(
        () =>
            [...userOptions].sort((a, b) => {
                const aName = `${a.first_name} ${a.last_name}`
                const bName = `${b.first_name} ${b.last_name}`

                return aName.localeCompare(bName)
            }),
        [userOptions],
    )

    return (
        <Box onClick={e => e.stopPropagation()}>
            <SharedAssigneeSelect
                assignee={chat.assignee ?? null}
                setAssignee={handleSetAssignee}
                userOptions={orderedUserOptions}
                isLoading={loading || isUpdating}
                size={size}
            />
        </Box>
    )
}
